import React, { useEffect, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { useLanguage } from "./LanguageContext";
import { FiCheckCircle } from "react-icons/fi";

const COLORS = {
  text: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.08)",
  subtle: "#f7f7f8",
  bg: "#ffffff",
  brand: "#a15a5a",
  brandDark: "#8e4a4a",
  success: "#16a34a",
};

function getLangFromQuery() {
  const q = new URLSearchParams(window.location.search).get("lang") || "";
  const v = q.toLowerCase();
  return v === "ka" || v === "en" ? v : null;
}

export default function VerifySuccess() {
  const ctx = useLanguage() || {};
  const languageFromCtx = ctx.language || "en";
  const setLanguage = ctx.setLanguage;

  const langFromQS = useMemo(getLangFromQuery, []);
  const effectiveLang = langFromQS || languageFromCtx;

  useEffect(() => {
    if (langFromQS && setLanguage && languageFromCtx !== langFromQS) {
      setLanguage(langFromQS);
    }
  }, [langFromQS, setLanguage, languageFromCtx]);

  const T = (ka, en) => (effectiveLang === "ka" ? ka : en);

  useEffect(() => {
    document.title =
      effectiveLang === "ka"
        ? "იმეილი დადასტურდა | Shopaholics"
        : "Email confirmed | Shopaholics";

    const t = setTimeout(() => {
      // გადამისამართებისასაც შევინარჩუნოთ ენა
      window.location.href = `/${`?lang=${effectiveLang}`}`;
    }, 5000);
    return () => clearTimeout(t);
  }, [effectiveLang]);

  const langSuffix = `?lang=${effectiveLang}`;

  return (
    <Page>
      <Card aria-live="polite">
        <Header>
          <IconWrap>
            <FiCheckCircle />
          </IconWrap>
          <div>
            <Title>✅ {T("იმეილი დადასტურდა", "Email confirmed")}</Title>
            <Subtle>{T("ახლა შეგიძლიათ შეხვიდეთ თქვენს პროფილში.", "You can now log in to your account.")}</Subtle>
          </div>
        </Header>

        <Timer>
          <TimerBar />
          <TimerText>{T("გადამისამართება 5 წამში...", "Redirecting in 5 seconds...")}</TimerText>
        </Timer>

        <Actions>
          <Primary as="a" href={`/login${langSuffix}`}>{T("შესვლა", "Log in")}</Primary>
          <Ghost as="a" href={`/${langSuffix}`}>{T("მთავარზე დაბრუნება", "Back to home")}</Ghost>
        </Actions>
      </Card>
    </Page>
  );
}

/* ====== styles (unchanged) ====== */

const Page = styled.main`
  background: ${COLORS.subtle};
  padding: clamp(12px, 4vw, 24px);
  display: grid;
  justify-items: center;
  align-content: center;
  overflow: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 640px) {
    align-content: start;
    padding-top: max(12px, env(safe-area-inset-top));
    padding-bottom: max(12px, env(safe-area-inset-bottom));
  }
`;

const Card = styled.section`
  width: min(560px, 100%);
  background: ${COLORS.bg};
  border: 1px solid ${COLORS.line};
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0,0,0,.08);
  padding: clamp(16px, 3.5vw, 28px);
  display: grid;
  gap: 14px;
  max-height: 100%;
  overflow: auto;
`;

const Header = styled.header`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const IconWrap = styled.div`
  width: 40px; height: 40px;
  border-radius: 12px;
  display: grid; place-items: center;
  font-size: 20px;
  color: #fff;
  background: ${COLORS.success};
  box-shadow: 0 8px 20px rgba(0,0,0,.08);
  flex: 0 0 auto;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(18px, 2.6vw, 22px);
  font-weight: 900;
  color: ${COLORS.text};
`;

const Subtle = styled.p`
  margin: 4px 0 0;
  color: ${COLORS.dim};
  font-size: 14px;
`;

const fill = keyframes`
  from { width: 0%; }
  to   { width: 100%; }
`;

const Timer = styled.div`
  background: ${COLORS.subtle};
  border: 1px solid ${COLORS.line};
  border-radius: 12px;
  padding: 10px 12px;
`;

const TimerBar = styled.div`
  height: 6px;
  background: ${COLORS.brand};
  border-radius: 8px;
  width: 0%;
  animation: ${fill} 5s linear forwards;
  box-shadow: 0 2px 8px rgba(0,0,0,.08) inset;
`;

const TimerText = styled.div`
  margin-top: 8px;
  color: ${COLORS.dim};
  font-size: 13px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 6px;
  a { flex: 1 1 180px; text-align: center; }
  @media (max-width: 420px) { a { flex: 1 1 100%; } }
`;

const ButtonBase = styled.a`
  height: 42px;
  padding: 0 16px;
  border-radius: 10px;
  font-weight: 800;
  font-size: 14px;
  text-decoration: none;
  display: inline-flex; align-items: center; justify-content: center;
  transition: transform .15s ease, box-shadow .15s ease, filter .15s ease;
  &:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(0,0,0,.08); }
`;

const Primary = styled(ButtonBase)`
  background: ${COLORS.brand};
  color: #fff;
  border: 1px solid ${COLORS.brand};
`;

const Ghost = styled(ButtonBase)`
  background: #fff;
  color: ${COLORS.text};
  border: 1px solid ${COLORS.line};
`;
